"use client";

import type { ReactNode } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { TherapyRelationshipType } from "~/prisma/browser";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";

export interface ReservationDetails {
  therapistName: string;
  therapistImage?: string;
  therapyType: TherapyRelationshipType;
  formattedDateTime: string;
}

export function ReservationCard({
  sessionDetails,
  sessionDurationMinutes,
  dateLabel = "Termin",
  footer,
}: {
  sessionDetails: ReservationDetails;
  sessionDurationMinutes?: number;
  dateLabel?: string;
  footer?: ReactNode;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const handleEditSession = () => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("step", "0");
    router.push(`?${params.toString()}`);
  };

  return (
    <div className="rounded-lg border bg-background p-4">
      <div className="flex items-start gap-3">
        <Avatar className="h-10 w-10 shrink-0">
          <AvatarImage
            src={sessionDetails.therapistImage}
            alt={sessionDetails.therapistName}
          />
          <AvatarFallback className="text-xs font-semibold">
            {sessionDetails.therapistName
              .split(" ")
              .map((name) => name[0])
              .join("")
              .slice(0, 2)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground">
            {sessionDetails.therapistName}
          </p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {sessionDetails.therapyType === TherapyRelationshipType.INDIVIDUAL
              ? "Individualna terapija"
              : "Terapija za parove"}
            {" · "}
            {sessionDurationMinutes ?? 45} min
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span className="font-medium text-foreground">
              {dateLabel}: {sessionDetails.formattedDateTime}
            </span>
            <button
              type="button"
              onClick={handleEditSession}
              className="text-sm text-brand underline-offset-4 hover:underline focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand"
            >
              Promeni
            </button>
          </div>
        </div>
      </div>
      {footer && (
        <div className="mt-4 border-t border-gray-200 pt-4">{footer}</div>
      )}
    </div>
  );
}
